import { EIRData, EIREntry, JournalNote, HealthMemoryItem } from '../types';
import { healthMemoryService } from '../services/healthMemory';
import { persistence } from 'services/persistence';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { toDateString } from 'utils/date';

// Export data as JSON
export const exportDataAsJSON = (data: EIRData, journalNotes: JournalNote[]): void => {
  const exportPayload: EIRData = {
    ...data,
    journal_entries: journalNotes.map((note) => ({
      id: note.id,
      title: note.title,
      content: note.content,
      created_at: note.createdAt,
      wellbeing_score: note.wellbeingScore
    }))
  };

  const dataStr = JSON.stringify(exportPayload, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);

  const link = document.createElement('a');
  link.href = url;
  link.download = 'eir-data-export.eir';
  link.click();

  URL.revokeObjectURL(url);
};

// Helper function to convert object to YAML format (matching backend format)
const convertToYAML = (obj: EIRData): string => {
  const safe = (v: any): string => {
    if (typeof v === 'string') {
      return `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`;
    }
    return String(v);
  };

  let yaml = '';

  // Metadata section
  if (obj.metadata) {
    yaml += 'metadata:\n';
    const md = obj.metadata;
    if (md.format_version) yaml += `  format_version: ${safe(md.format_version)}\n`;
    yaml += `  created_at: ${safe(md.created_at || new Date().toISOString())}\n`;
    if (md.source) yaml += `  source: ${safe(md.source)}\n`;

    if (md.patient) {
      yaml += '  patient:\n';
      if (md.patient.name) yaml += `    name: ${safe(md.patient.name)}\n`;
      if (md.patient.birth_date) yaml += `    birth_date: ${safe(md.patient.birth_date)}\n`;
      if (md.patient.personal_number) yaml += `    personal_number: ${safe(md.patient.personal_number)}\n`;
    }

    if (md.export_info) {
      const ei = md.export_info;
      yaml += '  export_info:\n';
      if (typeof ei.total_entries === 'number') yaml += `    total_entries: ${ei.total_entries}\n`;
      if (ei.date_range) {
        yaml += '    date_range:\n';
        if (ei.date_range.start) yaml += `      start: ${safe(ei.date_range.start)}\n`;
        if (ei.date_range.end) yaml += `      end: ${safe(ei.date_range.end)}\n`;
      }
      if (Array.isArray(ei.healthcare_providers)) {
        yaml += '    healthcare_providers:\n';
        ei.healthcare_providers.forEach((p) => {
          yaml += `      - ${safe(p)}\n`;
        });
      }
    }
  }

  // Entries section
  if (Array.isArray(obj.entries) && obj.entries.length > 0) {
    yaml += 'entries:\n';
    obj.entries.forEach((e) => {
      yaml += `-     id: ${safe(e.id || '')}\n`;
      if (e.date) yaml += `  date: ${safe(e.date)}\n`;
      if (e.time) yaml += `  time: ${safe(e.time)}\n`;
      if (e.category) yaml += `  category: ${safe(e.category)}\n`;
      if (e.type) yaml += `  type: ${safe(e.type)}\n`;
      if (e.provider) {
        yaml += '  provider:\n';
        if (e.provider.name) yaml += `    name: ${safe(e.provider.name)}\n`;
        if (e.provider.region) yaml += `    region: ${safe(e.provider.region)}\n`;
        if (e.provider.location) yaml += `    location: ${safe(e.provider.location)}\n`;
      }
      if (e.status) yaml += `  status: ${safe(e.status)}\n`;
      if (e.responsible_person) {
        yaml += '  responsible_person:\n';
        if (e.responsible_person.name) yaml += `    name: ${safe(e.responsible_person.name)}\n`;
        if (e.responsible_person.role) yaml += `    role: ${safe(e.responsible_person.role)}\n`;
      }
      if (e.content) {
        yaml += '  content:\n';
        if (e.content.summary) yaml += `    summary: ${safe(e.content.summary)}\n`;
        if (e.content.details) yaml += `    details: ${safe(e.content.details)}\n`;
        if (Array.isArray(e.content.notes) && e.content.notes.length > 0) {
          yaml += '    notes:\n';
          e.content.notes.forEach((n) => {
            yaml += `      - ${safe(n)}\n`;
          });
        }
      }
      if (Array.isArray(e.attachments) && e.attachments.length > 0) {
        yaml += '  attachments:\n';
        e.attachments.forEach((a) => yaml += `    - ${safe(a)}\n`);
      }
      if (Array.isArray(e.tags) && e.tags.length > 0) {
        yaml += '  tags:\n';
        e.tags.forEach((t) => yaml += `    - ${safe(t)}\n`);
      }
    });
  }

  // Extended sections
  if (Array.isArray(obj.journal_entries) && obj.journal_entries.length > 0) {
    yaml += 'journal_entries:\n';
    obj.journal_entries.forEach((note) => {
      yaml += `-     id: ${safe(note.id)}\n`;
      if (note.title) yaml += `  title: ${safe(note.title)}\n`;
      yaml += `  content: ${safe(note.content)}\n`;
      yaml += `  created_at: ${safe(note.created_at)}\n`;
      if (typeof note.wellbeing_score === 'number') yaml += `  wellbeing_score: ${note.wellbeing_score}\n`;
    });
  }

  if (Array.isArray(obj.chat_entries) && obj.chat_entries.length > 0) {
    yaml += 'chat_entries:\n';
    obj.chat_entries.forEach((entry) => {
      yaml += `-     id: ${safe(entry.id)}\n`;
      yaml += `  summary: ${safe(entry.summary)}\n`;
      yaml += `  timestamp: ${safe(entry.timestamp)}\n`;
      if (entry.transcript) yaml += `  transcript: ${safe(entry.transcript)}\n`;
      if (entry.aiDescription) yaml += `  aiDescription: ${safe(entry.aiDescription)}\n`;
    });
  }

  if (obj.health_assessments) {
    yaml += 'health_assessments:\n';
    if (obj.health_assessments.responses) {
      yaml += '  responses:\n';
      // Convert responses object to YAML
      Object.entries(obj.health_assessments.responses).forEach(([formId, responses]) => {
        yaml += `    ${formId}:\n`;
        Object.entries(responses as Record<string, number>).forEach(([questionId, value]) => {
          yaml += `      ${questionId}: ${value}\n`;
        });
      });
    }
    if (obj.health_assessments.results && typeof obj.health_assessments.results === 'object' && Object.keys(obj.health_assessments.results).length > 0) {
      yaml += '  results:\n';
      // Results are complex nested objects - serialize each form result
      Object.entries(obj.health_assessments.results).forEach(([formId, result]) => {
        if (result) {
          yaml += `    ${formId}: ${safe(JSON.stringify(result))}\n`;
        }
      });
    }
    // Don't add empty results section to YAML if no results exist
  }

  if (Array.isArray(obj.nutrition_entries) && obj.nutrition_entries.length > 0) {
    yaml += 'nutrition_entries:\n';
    obj.nutrition_entries.forEach((entry) => {
      yaml += `-     id: ${safe(entry.id)}\n`;
      yaml += `  name: ${safe(entry.name)}\n`;
      yaml += `  description: ${safe(entry.description)}\n`;
      yaml += `  timestamp: ${safe(entry.timestamp)}\n`;
      yaml += `  source: ${safe(entry.source)}\n`;
      if (typeof entry.calories === 'number') yaml += `  calories: ${entry.calories}\n`;
      if (typeof entry.carbohydrates === 'number') yaml += `  carbohydrates: ${entry.carbohydrates}\n`;
      if (typeof entry.protein === 'number') yaml += `  protein: ${entry.protein}\n`;
      if (typeof entry.fats === 'number') yaml += `  fats: ${entry.fats}\n`;
      if (typeof entry.fiber === 'number') yaml += `  fiber: ${entry.fiber}\n`;
      if (typeof entry.sugar === 'number') yaml += `  sugar: ${entry.sugar}\n`;
      if (typeof entry.hydration === 'number') yaml += `  hydration: ${entry.hydration}\n`;
      if (typeof entry.glucoseLevel === 'number') yaml += `  glucoseLevel: ${entry.glucoseLevel}\n`;
      if (entry.notes) yaml += `  notes: ${safe(entry.notes)}\n`;
    });
  }

  if (Array.isArray(obj.health_actions) && obj.health_actions.length > 0) {
    yaml += 'health_actions:\n';
    obj.health_actions.forEach((action) => {
      yaml += `-     id: ${safe(action.id)}\n`;
      yaml += `  title: ${safe(action.title)}\n`;
      yaml += `  description: ${safe(action.description)}\n`;
      yaml += `  category: ${safe(action.category)}\n`;
      yaml += `  estimatedTime: ${safe(action.estimatedTime)}\n`;
      yaml += `  difficulty: ${safe(action.difficulty)}\n`;
      yaml += `  completed: ${action.completed}\n`;
      yaml += `  createdAt: ${safe(action.createdAt)}\n`;
      if (action.completedAt) yaml += `  completedAt: ${safe(action.completedAt)}\n`;
      if (action.source) yaml += `  source: ${safe(action.source)}\n`;
      if (Array.isArray(action.benefits) && action.benefits.length > 0) {
        yaml += '  benefits:\n';
        action.benefits.forEach((benefit) => yaml += `    - ${safe(benefit)}\n`);
      }
      if (Array.isArray(action.instructions) && action.instructions.length > 0) {
        yaml += '  instructions:\n';
        action.instructions.forEach((instruction) => yaml += `    - ${safe(instruction)}\n`);
      }
      if (Array.isArray(action.tips) && action.tips.length > 0) {
        yaml += '  tips:\n';
        action.tips.forEach((tip) => yaml += `    - ${safe(tip)}\n`);
      }
    });
  }

  if (Array.isArray(obj.health_memory_items) && obj.health_memory_items.length > 0) {
    yaml += 'health_memory_items:\n';
    obj.health_memory_items.forEach((item: any) => {
      yaml += `-     id: ${safe(item.id)}\n`;
      yaml += `  category: ${safe(item.category)}\n`;
      yaml += `  label: ${safe(item.label)}\n`;
      if (item.detail) yaml += `  detail: ${safe(item.detail)}\n`;
      yaml += `  source_type: ${safe(item.source_type)}\n`;
      yaml += `  confidence: ${item.confidence}\n`;
      yaml += `  certainty_level: ${safe(item.certainty_level)}\n`;
      yaml += `  status: ${safe(item.status)}\n`;
      yaml += `  evidence_refs: ${safe(JSON.stringify(item.evidence_refs || []))}\n`;
      yaml += `  observed_at: ${safe(item.observed_at)}\n`;
      yaml += `  updated_at: ${safe(item.updated_at)}\n`;
      if (item.last_used_at) yaml += `  last_used_at: ${safe(item.last_used_at)}\n`;
    });
  }

  return yaml;
};

const collectExtendedEntries = () => {
  let chatEntries: any[] | undefined;
  let healthAssessments: { responses?: Record<string, Record<string, number>>; results?: Record<string, any> } | undefined;
  let nutritionEntries: any[] | undefined;
  let healthActions: any[] | undefined;

  try {
    const parsedChat = persistence.getJSON<any[] | null>('eir-chat-journal', null);
    if (parsedChat) {
      chatEntries = parsedChat;
    }

    const responsesStr = persistence.getItem(STORAGE_KEYS.HEALTH_ASSESSMENT_RESPONSES);
    const resultsStr = persistence.getItem(STORAGE_KEYS.HEALTH_ASSESSMENT_RESULTS);

    console.log('[collectExtendedEntries] localStorage check:', {
      hasResponses: !!responsesStr,
      hasResults: !!resultsStr,
      responsesLength: responsesStr?.length,
      resultsLength: resultsStr?.length,
      resultsFirstChars: resultsStr?.substring(0, 100)
    });

    if (responsesStr || resultsStr) {
      healthAssessments = {};
      if (responsesStr) {
        try {
          const parsed = JSON.parse(responsesStr);
          if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
            healthAssessments.responses = parsed;
          }
        } catch (e) {
          console.warn('[collectExtendedEntries] Failed to parse health assessment responses:', e);
        }
      }
      if (resultsStr) {
        try {
          const parsed = JSON.parse(resultsStr);
          console.log('[collectExtendedEntries] Parsed results:', {
            type: typeof parsed,
            isObject: typeof parsed === 'object',
            isNull: parsed === null,
            keys: parsed ? Object.keys(parsed) : [],
            keysLength: parsed ? Object.keys(parsed).length : 0
          });

          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length > 0) {
            healthAssessments.results = parsed;
          }
        } catch (e) {
          console.warn('[collectExtendedEntries] Failed to parse health assessment results:', e);
        }
      }
    }

    const parsedNutrition = persistence.getJSON<any[] | null>('eir-nutrition-meal-log', null);
    if (parsedNutrition) {
      nutritionEntries = parsedNutrition;
    }

    const parsedActions = persistence.getJSON<any[] | null>(STORAGE_KEYS.HEALTH_ACTIONS, null);
    if (parsedActions) {
      healthActions = parsedActions;
    }
  } catch (error) {
    console.warn('Error loading extended entries from localStorage for export:', error);
  }

  return { chatEntries, healthAssessments, nutritionEntries, healthActions };
};

// Helper function to check if an entry is valid (not empty/invalid)
const isValidEntry = (entry: any): boolean => {
  if (!entry || !entry.id) return false;

  // Filter out entries that look like health actions (they have category but no date/provider)
  // Health actions have categories like "Mental Health", "Movement", "Breathing", etc.
  // but they shouldn't be in the entries array - they should be in health_actions
  const healthActionCategories = ['Mental Health', 'Movement', 'Breathing', 'Mindfulness', 'Hydration', 'Relationship', 'Stretching'];
  if (healthActionCategories.includes(entry.category) &&
      (!entry.date || entry.date === 'Unknown' || entry.date === 'Unknown Date') &&
      (!entry.provider?.name || entry.provider.name === 'Unknown' || entry.provider.name === 'Unknown Provider')) {
    // This looks like a health action that was incorrectly parsed as an entry
    return false;
  }

  // Filter out entries that are mostly "Unknown" values (likely invalid/empty entries)
  const hasValidDate = entry.date && entry.date !== 'Unknown' && entry.date !== 'Unknown Date' && entry.date !== 'unknown date';
  const hasValidContent = entry.content && (
    (entry.content.summary && entry.content.summary !== 'No summary available') ||
    (entry.content.details && entry.content.details.trim().length > 0) ||
    (Array.isArray(entry.content.notes) && entry.content.notes.length > 0)
  );

  // Entry is valid if it has at least a valid date OR valid content
  return hasValidDate || hasValidContent;
};

function toSnakeCaseHealthMemoryItem(item: HealthMemoryItem): Record<string, unknown> {
  return {
    id: item.id,
    category: item.category,
    label: item.label,
    ...(item.detail ? { detail: item.detail } : {}),
    source_type: item.sourceType,
    confidence: item.confidence,
    certainty_level: item.certaintyLevel,
    status: item.status,
    evidence_refs: item.evidenceRefs,
    observed_at: item.observedAt,
    updated_at: item.updatedAt,
    ...(item.lastUsedAt ? { last_used_at: item.lastUsedAt } : {}),
  };
}

export const buildEirExportPayload = (
  data: EIRData,
  journalNotes?: JournalNote[],
  healthMemoryItems?: HealthMemoryItem[]
): EIRData => {
  const { chatEntries, healthAssessments, nutritionEntries, healthActions } = collectExtendedEntries();

  // Filter out invalid/empty entries before exporting
  const validEntries = (data.entries || []).filter(isValidEntry);

  return {
    ...data,
    entries: validEntries,
    journal_entries: journalNotes?.map((note) => ({
      id: note.id,
      title: note.title,
      content: note.content,
      created_at: note.createdAt,
      wellbeing_score: note.wellbeingScore
    })) || data.journal_entries || [],
    // Include extended entry types if they exist
    ...(chatEntries && chatEntries.length > 0 ? { chat_entries: chatEntries } : {}),
    ...(healthAssessments ? { health_assessments: healthAssessments } : {}),
    ...(nutritionEntries && nutritionEntries.length > 0 ? { nutrition_entries: nutritionEntries } : {}),
    ...(healthActions && healthActions.length > 0 ? { health_actions: healthActions } : {}),
    ...(healthMemoryItems && healthMemoryItems.length > 0
      ? { health_memory_items: healthMemoryItems.map(toSnakeCaseHealthMemoryItem) }
      : {}),
  };
};

export const createEirYamlSnapshot = (
  data: EIRData,
  journalNotes?: JournalNote[],
  healthMemoryItems?: HealthMemoryItem[]
): string => {
  const exportPayload = buildEirExportPayload(data, journalNotes, healthMemoryItems);
  return convertToYAML(exportPayload);
};

// Export data as EIR file (YAML format)
export const downloadEirFile = async (
  data: EIRData,
  journalNotes?: JournalNote[],
  healthMemoryItems?: HealthMemoryItem[]
): Promise<void> => {
  let items = healthMemoryItems;
  if (items === undefined && typeof window !== 'undefined' && healthMemoryService.isEnabled()) {
    items = await healthMemoryService.loadItems();
  }
  const yamlStr = createEirYamlSnapshot(data, journalNotes, items);

  const dataBlob = new Blob([yamlStr], { type: 'text/yaml' });
  const url = URL.createObjectURL(dataBlob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `journal-content-${toDateString()}.eir`;
  link.click();

  URL.revokeObjectURL(url);
};

// Export data as TXT file (readable format)
export const downloadTxtFile = (data: EIRData, journalNotes?: JournalNote[]): void => {
  const patientName = data.metadata?.patient?.name || 'Unknown';
  const entries = data.entries || [];
  const notes = journalNotes || [];

  let content = `=== 1177 JOURNAL DOWNLOAD ===
Downloaded: ${new Date().toLocaleString('sv-SE')}
Patient: ${patientName}
Total Entries: ${entries.length}

========================================

`;

  // Add journal entries
  entries.forEach((entry, index) => {
    content += `\n--- ENTRY ${index + 1} ---\n`;

    if (entry.date) {
      content += `Date: ${entry.date}\n`;
    }

    if (entry.time) {
      content += `Time: ${entry.time}\n`;
    }

    if (entry.type) {
      content += `Type: ${entry.type}\n`;
    }

    if (entry.category) {
      content += `Category: ${entry.category}\n`;
    }

    if (entry.provider?.name) {
      content += `Provider: ${entry.provider.name}\n`;
    }

    if (entry.status) {
      content += `Status: ${entry.status}\n`;
    }

    if (entry.content?.summary) {
      content += `\nSummary:\n${entry.content.summary}\n`;
    }

    if (entry.content?.details) {
      content += `\nDetails:\n${entry.content.details}\n`;
    }

    if (entry.content?.notes && Array.isArray(entry.content.notes) && entry.content.notes.length > 0) {
      content += `\nNotes:\n${entry.content.notes.join('\n')}\n`;
    }

    if (entry.tags && entry.tags.length > 0) {
      content += `\nTags: ${entry.tags.join(', ')}\n`;
    }

    content += `\n${'='.repeat(50)}\n`;
  });

  // Add journal notes if any
  if (notes.length > 0) {
    content += `\n\n=== JOURNAL NOTES ===\n`;
    notes.forEach((note, index) => {
      content += `\n--- NOTE ${index + 1} ---\n`;
      content += `Date: ${new Date(note.createdAt).toLocaleString('sv-SE')}\n`;
      content += `Wellbeing Score: ${note.wellbeingScore}/10\n`;
      if (note.title) {
        content += `Title: ${note.title}\n`;
      }
      content += `\n${note.content}\n`;
      content += `\n${'='.repeat(50)}\n`;
    });
  }

  // Add metadata
  content += `\n\n=== METADATA ===\n`;
  content += `Source: ${data.metadata?.source || 'Unknown'}\n`;
  content += `Created: ${data.metadata?.created_at || 'Unknown'}\n`;
  if (data.metadata?.patient) {
    if (data.metadata.patient.birth_date) {
      content += `Birth Date: ${data.metadata.patient.birth_date}\n`;
    }
    if (data.metadata.patient.personal_number) {
      content += `Personal Number: ${data.metadata.patient.personal_number}\n`;
    }
  }

  const dataBlob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(dataBlob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `journal-content-${toDateString()}.txt`;
  link.click();

  URL.revokeObjectURL(url);
};
